// While a cat trips, the race keeps going: a live inset shows the normal field from a high wide
// camera, drawn into a corner of the canvas after post-processing, so neither the trip effect nor
// its warp touch it. It grows briefly when the lead changes. At touchdown a big call-out says how
// far the catnip moved the cat, in metres (e.g. "+228 m").
import { AgXToneMapping, type Object3D, PerspectiveCamera, type Scene, Vector2, Vector3, type WebGLRenderer } from 'three';
import { catById } from '../../data/cats';
import { LANE_WIDTH, laneCenterZ, posToX } from '../../data/track';
import { formatDelta } from '../../hud/format';
import type { CatId, RaceSnapshot } from '../../race/types';
import { renderStep } from '../../app/renderStep';

/** The live shot follows the cats on the ground within this many metres of the leading one. */
const PACK_M = 12;

const _size = new Vector2();
const STYLE_ID = 'catnip-trip-style';
const CSS = `
.trip-inset { position: absolute; pointer-events: none; border: 2px solid #f2c35b; border-radius: 10px;
  box-shadow: 0 6px 24px #0008, inset 0 0 0 1px #0006; opacity: 0; transition: opacity .35s ease;
  box-sizing: border-box; overflow: hidden; }
.trip-inset > span { position: absolute; left: 8px; top: 6px; padding: 2px 8px; border-radius: 6px;
  background: #211923e6; color: #fff3dc; font: 700 12px/1.4 system-ui, sans-serif; letter-spacing: .08em; }
.trip-inset > span::before { content: ''; display: inline-block; width: 7px; height: 7px; margin-right: 6px;
  border-radius: 50%; background: #ff4d5e; vertical-align: 1px; }
.trip-callout { position: absolute; left: 50%; top: 24%; transform: translate(-50%, -50%) scale(.9);
  pointer-events: none; text-align: center; opacity: 0; transition: opacity .3s ease, transform .45s cubic-bezier(.2,1.6,.4,1);
  color: #fff3dc; white-space: nowrap; padding: 14px 28px 16px; border-radius: 16px;
  background: linear-gradient(180deg, #2a1230f2, #1a0c1ef2); border: 2px solid #c77dff;
  box-shadow: 0 10px 36px #000b, 0 0 0 4px #c77dff33, 0 0 40px #c77dff55; }
.trip-callout.show { opacity: 1; transform: translate(-50%, -50%) scale(1); }
.trip-callout b { display: block; font: 900 clamp(32px, 5.2vw, 72px)/1 system-ui, sans-serif; letter-spacing: -.01em;
  color: #fff3dc; text-shadow: 0 2px 0 #450f41, -2px 0 0 #ff4d6d99, 2px 0 0 #3ff0ff99; }
.trip-callout small { display: block; margin-top: 8px; font: 700 clamp(15px, 1.6vw, 22px)/1.2 system-ui, sans-serif; color: #f1dcff; }
@media (max-width: 560px) { .trip-callout { white-space: normal; width: max-content; max-width: calc(100vw - 32px); box-sizing: border-box; padding: 10px 18px 12px; } }
@media (prefers-reduced-motion: reduce) { .trip-callout { transition: opacity .3s ease; } }
`;

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = CSS;
  document.head.appendChild(s);
}

/** The deadpan line under the landing figure. Never predicts disqualification. Pure. */
export function landingLine(name: string, delta: number, placeAfter: number | null, to: number): string {
  if (placeAfter === 1 && delta > 0) return `${name} lands straight in the lead`;
  if (to < 0.08 && delta < 0) return `${name} is back at the start`;
  if (Math.abs(delta) < 0.03) return `${name} lands right where it started`;
  return delta > 0 ? `${name} lands well ahead` : `${name} lands back in the pack`;
}

export interface Runner { x: number; z: number }

/**
 * The group of cats to show live: cats within PACK_M behind one of them along the track and at most
 * one lane to either side of it, so a chase camera can hold them all. The group with the most cats
 * wins, the one further ahead on a tie. `runners` sorted by x, leader first.
 */
export function packOf<T extends Runner>(runners: readonly T[]): T[] {
  let best: T[] = runners.slice(0, 1);
  for (const a of runners) {
    const group = runners.filter((r) => r.x <= a.x && a.x - r.x <= PACK_M && Math.abs(r.z - a.z) <= LANE_WIDTH * 1.01);
    if (group.length > best.length) best = group;
  }
  return best;
}

export class LiveInset {
  readonly camera = new PerspectiveCamera(40, 16 / 9, 0.2, 3000);
  private frame: HTMLDivElement;
  private callout: HTMLDivElement;
  private shown = 0;
  private want = false;
  private grow = 0;
  private calloutUntil = 0;
  private target = new Vector3();
  private pos = new Vector3();
  private placed = false;
  private clock = 0;
  private lastT = Number.NaN;
  private packKey = '';
  private packLast = 0;

  constructor(container: HTMLElement) {
    ensureStyle();
    if (getComputedStyle(container).position === 'static') container.style.position = 'relative';
    this.frame = document.createElement('div');
    this.frame.className = 'trip-inset';
    this.frame.innerHTML = '<span>LIVE RACE</span>';
    this.frame.setAttribute('aria-hidden', 'true');
    this.callout = document.createElement('div');
    this.callout.className = 'trip-callout';
    this.callout.setAttribute('role', 'status');
    container.append(this.frame, this.callout);
  }

  /** Show the inset (while a cat is up in the air) and frame the rest of the field. */
  update(snap: RaceSnapshot, show: boolean, leadChanged: boolean, exclude: CatId | null, dt: number): void {
    // Ease on race render time, not the loop's capped dt, so the shot keeps up with the race at any frame rate.
    const step = renderStep(this.lastT, snap.t, dt);
    this.lastT = snap.t;
    this.clock += step;
    this.want = show;
    if (show && this.shown < 0.04) this.placed = false; // cut straight to the field when it opens
    this.shown += ((show ? 1 : 0) - this.shown) * (1 - Math.exp(-step / 0.25));
    if (leadChanged && show) this.grow = 1;
    this.grow = Math.max(0, this.grow - step / 2.5);
    // A low chase shot from just behind the biggest group of cats still running, looking up the
    // track over them: the nearest cats fill the picture and the rest of the group runs ahead.
    const runners = snap.cats.filter((c) => c.id !== exclude && !c.tripping)
      .map((c) => ({ id: c.id, x: posToX(c.displayPos), z: laneCenterZ(c.lane) })).sort((a, b) => b.x - a.x);
    const pack = packOf(runners);
    const lead = pack.length ? pack[0]!.x : 0;
    const last = pack.length ? pack[pack.length - 1]!.x : 0;
    const zs = pack.map((r) => r.z);
    const zMid = zs.length ? (Math.max(...zs) + Math.min(...zs)) / 2 : 0;
    const zSpan = zs.length ? Math.max(...zs) - Math.min(...zs) : 0;
    // low, just above the cats' backs, aimed a few metres past the rearmost so it stays in frame
    const back = 3.4 + zSpan * 0.3;
    const t = new Vector3(Math.max(lead, last + 6) + 1, 0.25, zMid);
    const p = new Vector3(last - back, 0.7 + back * 0.05, zMid + 0.4);
    // The camera runs with the group (cats cover about 10 m/s, so easing alone would trail them by
    // metres) and only eases its framing, or across to a new group.
    const key = pack.map((r) => r.id).join();
    if (this.placed && key === this.packKey) { this.pos.x += last - this.packLast; this.target.x += last - this.packLast; }
    this.packKey = key;
    this.packLast = last;
    if (!this.placed) { this.target.copy(t); this.pos.copy(p); this.placed = true; }
    const k = 1 - Math.exp(-step * 3);
    this.target.lerp(t, k);
    this.pos.lerp(p, k);
    this.camera.position.copy(this.pos);
    this.camera.lookAt(this.target);
    if (this.calloutUntil && this.clock > this.calloutUntil) { this.callout.classList.remove('show'); this.calloutUntil = 0; }
  }

  /** The big call-out at touchdown, or "Again?!" when a cat catches the catnip straight after landing. */
  announce(title: string, line: string, seconds = 3.2): void {
    this.callout.innerHTML = '';
    const b = document.createElement('b');
    b.textContent = title;
    const s = document.createElement('small');
    s.textContent = line;
    this.callout.append(b, s);
    void this.callout.offsetWidth; // restart the pop-in
    this.callout.classList.add('show');
    this.calloutUntil = this.clock + seconds;
  }

  announceLanding(cat: CatId, delta: number, to: number, placeAfter: number | null): void {
    const name = catById(cat).shortName;
    this.announce(formatDelta(delta), landingLine(name, delta, placeAfter, to));
  }

  /** The inset's rectangle in CSS pixels of the canvas, or null when hidden. */
  rect(width: number, height: number): { x: number; y: number; w: number; h: number } | null {
    if (this.shown < 0.04) return null;
    const scale = 1 + 0.22 * Math.sin(Math.min(1, this.grow) * Math.PI * 0.5);
    // A tall phone screen: wider, and lower, below the standings at the top
    const tall = height > width;
    const w = Math.round(Math.min(width * (tall ? 0.46 : 0.27), 520) * scale);
    const h = Math.round(w * 9 / 16);
    const x = Math.round(width - w - width * 0.018);
    const y = Math.round(height * (tall ? 0.3 : 0.085));
    return { x, y, w, h };
  }

  /**
   * Draws the inset over the finished frame. `normal()` puts the world into its everyday look
   * (trip sky off, aura hidden) and `restore()` undoes it. The renderer tone-maps here with AgX,
   * like the post chain, because the inset skips post-processing.
   */
  render(renderer: WebGLRenderer, scene: Scene, normal: () => void, restore: () => void): void {
    const size = renderer.getSize(_size);
    const r = this.rect(size.x, size.y);
    this.frame.style.opacity = r ? String(Math.min(1, this.shown * 1.2)) : '0';
    if (!r) return;
    Object.assign(this.frame.style, { left: `${r.x}px`, top: `${r.y}px`, width: `${r.w}px`, height: `${r.h}px` });
    if (this.shown < 0.15 && !this.want) return;
    this.camera.aspect = r.w / r.h;
    this.camera.updateProjectionMatrix();
    const prevTone = renderer.toneMapping;
    const prevAuto = renderer.autoClear;
    normal();
    renderer.setRenderTarget(null);
    renderer.toneMapping = AgXToneMapping;
    renderer.autoClear = true;
    const yGl = size.y - r.y - r.h; // viewport origin is bottom left
    renderer.setScissorTest(true);
    renderer.setScissor(r.x, yGl, r.w, r.h);
    renderer.setViewport(r.x, yGl, r.w, r.h);
    renderer.render(scene, this.camera);
    renderer.setScissorTest(false);
    renderer.setViewport(0, 0, size.x, size.y);
    renderer.toneMapping = prevTone;
    renderer.autoClear = prevAuto;
    restore();
  }

  /** Compiles the inset's tone-mapped shader variants up front, so the first trip does not stall. */
  async compile(renderer: WebGLRenderer, scene: Scene | Object3D): Promise<void> {
    const prev = renderer.toneMapping;
    renderer.toneMapping = AgXToneMapping;
    try { await renderer.compileAsync(scene, this.camera); } finally { renderer.toneMapping = prev; }
  }

  dispose(): void { this.frame.remove(); this.callout.remove(); }
}
